/**
 * JSON-LD structured data components for rich Google search results.
 * Schema.org types: MusicRecording, MusicAlbum, MusicGroup, Event, NewsArticle
 */

interface MusicRecordingLDProps {
  id: string;
  name: string;
  artist: string;
  albumName?: string;
  duration?: number; // in milliseconds
  genre?: string;
  imageUrl?: string;
  url: string;
}

export function MusicRecordingLD({ id, name, artist, albumName, duration, genre, imageUrl, url }: MusicRecordingLDProps) {
  const durationISO = duration ? `PT${Math.floor(duration / 60000)}M${Math.floor((duration % 60000) / 1000)}S` : undefined;

  const data = {
    '@context': 'https://schema.org',
    '@type': 'MusicRecording',
    '@id': url,
    name,
    byArtist: {
      '@type': 'MusicGroup',
      name: artist,
    },
    ...(albumName && { inAlbum: { '@type': 'MusicAlbum', name: albumName } }),
    ...(durationISO && { duration: durationISO }),
    ...(genre && { genre }),
    ...(imageUrl && { image: imageUrl }),
    url,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

interface MusicAlbumLDProps {
  name: string;
  artist: string;
  year?: number;
  genre?: string;
  imageUrl?: string;
  url: string;
  tracks?: Array<{ name: string; position: number; duration?: number }>;
}

export function MusicAlbumLD({ name, artist, year, genre, imageUrl, url, tracks }: MusicAlbumLDProps) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'MusicAlbum',
    name,
    byArtist: { '@type': 'MusicGroup', name: artist },
    ...(year && { datePublished: String(year) }),
    ...(genre && { genre }),
    ...(imageUrl && { image: imageUrl }),
    url,
    ...(tracks && tracks.length > 0 && {
      track: tracks.map(t => ({
        '@type': 'MusicRecording',
        name: t.name,
        position: t.position,
        ...(t.duration && {
          duration: `PT${Math.floor(t.duration / 60000)}M${Math.floor((t.duration % 60000) / 1000)}S`,
        }),
      })),
      numTracks: tracks.length,
    }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

interface MusicGroupLDProps {
  name: string;
  genre?: string;
  imageUrl?: string;
  url: string;
  description?: string;
  sameAs?: string[]; // social links
}

export function MusicGroupLD({ name, genre, imageUrl, url, description, sameAs }: MusicGroupLDProps) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'MusicGroup',
    name,
    ...(genre && { genre }),
    ...(imageUrl && { image: imageUrl }),
    url,
    ...(description && { description }),
    ...(sameAs && sameAs.length > 0 && { sameAs }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

interface NewsArticleLDProps {
  headline: string;
  description?: string;
  imageUrl?: string;
  url: string;
  publishedAt?: string; // ISO date
  modifiedAt?: string;
  authorName?: string;
}

export function NewsArticleLD({ headline, description, imageUrl, url, publishedAt, modifiedAt, authorName }: NewsArticleLDProps) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline,
    ...(description && { description }),
    ...(imageUrl && { image: imageUrl }),
    url,
    ...(publishedAt && { datePublished: publishedAt }),
    ...(modifiedAt && { dateModified: modifiedAt }),
    ...(authorName && { author: { '@type': 'Person', name: authorName } }),
    publisher: {
      '@type': 'Organization',
      name: "Breakin' Bread",
      logo: {
        '@type': 'ImageObject',
        url: 'https://breakinbread.com/icons/icon-192.png',
      },
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

interface ProductLDProps {
  name: string;
  description?: string;
  imageUrl?: string;
  url: string;
  price?: number; // in cents
  currency?: string;
  sellerName?: string;
}

export function ProductLD({ name, description, imageUrl, url, price, currency = 'USD', sellerName }: ProductLDProps) {
  const data = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    ...(description && { description }),
    ...(imageUrl && { image: imageUrl }),
    url,
    ...(price !== undefined && {
      offers: {
        '@type': 'Offer',
        price: (price / 100).toFixed(2),
        priceCurrency: currency.toUpperCase(),
        availability: 'https://schema.org/InStock',
        ...(sellerName && { seller: { '@type': 'Organization', name: sellerName } }),
      },
    }),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}
